import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Lock, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { useCart, clearCart } from "@/lib/cart/store";
import { formatPrice, hasFreeShippingPack } from "@/lib/cart/pricing";
import { PromoInput } from "@/components/cart/PromoInput";
import { CartPriceNotice } from "@/components/cart/CartPriceNotice";
import { useCartPriceSync } from "@/lib/cart/use-price-sync";
import { CheckoutField } from "@/components/checkout/CheckoutField";
import { AddressFieldset } from "@/components/checkout/AddressFieldset";
import { OptionCard } from "@/components/checkout/OptionCard";
import { CheckoutSummary } from "@/components/checkout/CheckoutSummary";
import { calcCheckoutPricing } from "@/lib/checkout/pricing";
import {
  deliveryCharge,
  getDeliveryOptions,
  checkoutSchema,
  type Address,
  type CheckoutData,
} from "@/lib/checkout/schema";
import { submitOrder } from "@/lib/checkout/order";
import { readMarketing, clearMarketing } from "@/lib/checkout/marketing";
import { openRazorpayCheckout, loadRazorpay, RAZORPAY_LOAD_ERROR } from "@/lib/payments/checkout-loader";
import { usePromoCode, useActiveOffers } from "@/lib/cart/use-promo";
import { useAddressBook, type SavedAddress } from "@/lib/account/addresses";
import { SavedAddressPicker } from "@/components/checkout/SavedAddressPicker";
import { DeviceAddressesNotice } from "@/components/account/DeviceAddressesNotice";
import {
  startPaymentIntent,
  verifyPayment,
  cancelPaymentIntent,
} from "@/lib/payments/intent.functions";
import { track, identify } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";
import { useShippingConfig, useStoreSettings } from "@/lib/admin/settings";
import { gstConfigFromSettings } from "@/lib/tax/config";


export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Checkout — CB16 Maison" },
      {
        name: "description",
        content: "Complete your CB16 Maison order — shipping, delivery, and secure payment.",
      },
      { property: "og:title", content: "Checkout — CB16 Maison" },
      { property: "og:description", content: "Complete your CB16 Maison order securely." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CheckoutPage,
});

const EMPTY_ADDRESS: Address = {
  fullName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "India",
  phone: "",
};

type FormState = {
  email: string;
  marketingOptIn: boolean;
  gstin: string;
  shipping: Address;
  billingSameAsShipping: boolean;
  billing: Address;
  delivery: CheckoutData["delivery"]["method"];
  paymentMethod: CheckoutData["payment"]["method"];
};

const INITIAL: FormState = {
  email: "",
  marketingOptIn: false,
  gstin: "",
  shipping: { ...EMPTY_ADDRESS },
  billingSameAsShipping: true,
  billing: { ...EMPTY_ADDRESS },
  delivery: "standard",
  paymentMethod: "razorpay",
};


function CheckoutPage() {
  const lines = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL);
  const promo = usePromoCode(lines);
  const offers = useActiveOffers();
  const book = useAddressBook();
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [saveNewAddress, setSaveNewAddress] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Bring saved bag prices up to date before payment (server still re-validates).
  const priceSync = useCartPriceSync(lines);
  const priceBlocked = !priceSync.synced || priceSync.changes.length > 0 || priceSync.unavailable.length > 0;

  const startFn = useServerFn(startPaymentIntent);
  const verifyFn = useServerFn(verifyPayment);
  const cancelFn = useServerFn(cancelPaymentIntent);

  const settings = useStoreSettings();
  const gstConfig = useMemo(() => gstConfigFromSettings(settings), [settings]);
  const shippingConfig = useShippingConfig();
  const deliveryOptions = useMemo(() => getDeliveryOptions(shippingConfig), [shippingConfig]);
  // Place of supply = delivery state (falls back to billing when billing differs).
  const placeOfSupply = form.shipping.state || (form.billingSameAsShipping ? "" : form.billing.state);
  const supplyCountry = form.shipping.country || form.billing.country;

  const { pricing, delivery } = useMemo(
    () =>
      calcCheckoutPricing(lines, form.delivery, promo.applied, {
        config: gstConfig,
        state: placeOfSupply,
        country: supplyCountry,
        customerGstin: form.gstin,
      }, shippingConfig),
    [lines, form.delivery, promo.applied, gstConfig, placeOfSupply, supplyCountry, form.gstin, shippingConfig],
  );


  // Preload the payment window so Place Order opens it straight away.
  useEffect(() => {
    void loadRazorpay().catch(() => {});
  }, []);

  // Use the customer's default saved address when they arrive with an empty form.
  useEffect(() => {
    if (book.loading || selectedAddressId || book.addresses.length === 0) return;
    if (form.shipping.line1.trim()) return;
    const initial = book.defaultAddress;
    if (initial) selectSavedAddress(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book.loading, book.addresses]);

  // Keep the shipping details in step when the selected saved address is edited.
  useEffect(() => {
    if (!selectedAddressId) return;
    const current = book.addresses.find((a) => a.id === selectedAddressId);
    if (!current) {
      setSelectedAddressId(null);
      return;
    }
    setForm((f) => ({ ...f, shipping: toShipping(current) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book.addresses]);

  function selectSavedAddress(a: SavedAddress) {
    setSelectedAddressId(a.id);
    setForm((f) => ({ ...f, shipping: toShipping(a) }));
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith("shipping."))));
  }

  // Checkout entry — fired once per checkout view.
  useEffect(() => {
    if (lines.length === 0) return;
    track(CIO_EVENTS.CHECKOUT_STARTED, {
      items: lines.length,
      subtotal: pricing.subtotal,
      grand_total: pricing.total,
      currency: "INR",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (lines.length === 0) {
    return <EmptyState />;
  }

  function pickError(path: string) {
    return errors[path];
  }
  function addressErrors(prefix: "shipping" | "billing") {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(errors)) {
      if (k.startsWith(prefix + ".")) out[k.slice(prefix.length + 1)] = v;
    }
    return out as Partial<Record<keyof Address, string>>;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (priceBlocked || promo.pending) return;

    const payload: CheckoutData = {
      customer: {
        email: form.email,
        marketingOptIn: form.marketingOptIn,
        ...(form.gstin.trim() ? { gstin: form.gstin.trim().toUpperCase() } : {}),
      },
      shipping: form.shipping,

      billingSameAsShipping: form.billingSameAsShipping,
      billing: form.billingSameAsShipping ? undefined : form.billing,
      delivery: { method: form.delivery },
      payment: {
        method: form.paymentMethod,
        cardName: "",
        cardNumberLast4: "",
      },
    };

    const parsed = checkoutSchema.safeParse(payload);
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        map[issue.path.join(".")] = issue.message;
      }
      setErrors(map);
      const firstKey = Object.keys(map)[0];
      if (firstKey) {
        const el = document.querySelector<HTMLElement>(`[name="${firstKey.split(".").pop()}"]`);
        el?.focus();
      }
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const billing =
        parsed.data.billingSameAsShipping || !parsed.data.billing
          ? parsed.data.shipping
          : parsed.data.billing;
      const marketing = readMarketing() ?? {};
      const currency = "INR";

      identify(parsed.data.customer.email, null, {
        phone: parsed.data.shipping.phone,
        city: parsed.data.shipping.city,
        country: parsed.data.shipping.country,
        marketing_consent: parsed.data.customer.marketingOptIn,
      });
      track(CIO_EVENTS.SHIPPING_SELECTED, {
        email: parsed.data.customer.email,
        delivery_type: delivery.label,
        shipping: pricing.shipping,
        currency,
      });
      if (pricing.promo?.code) {
        track(CIO_EVENTS.COUPON_APPLIED, {
          email: parsed.data.customer.email,
          coupon: pricing.promo.code,
          discount: pricing.discount,
          currency,
        });
      }

      // Load the payment window before reserving stock, so a blocked or slow
      // script never leaves an unpaid order attempt behind.
      try {
        await loadRazorpay();
      } catch {
        setSubmitError(RAZORPAY_LOAD_ERROR);
        setSubmitting(false);
        return;
      }

      // Signed-in customers typing a new address can keep it for next time.
      if (book.signedIn && !selectedAddressId && saveNewAddress && book.addresses.length < 20) {
        try {
          const saved = await book.save({
            ...parsed.data.shipping,
            label: "Home",
            isDefault: book.addresses.length === 0,
          });
          setSelectedAddressId(saved.id);
        } catch {
          /* saving the address is optional; never block payment */
        }
      }

      const intent = await startFn({
        data: {
          email: parsed.data.customer.email,
          lines: lines.map((l) => ({
            productId: l.productId,
            slug: l.slug,
            name: l.name,
            variantId: l.variantId,
            variantLabel: l.variantLabel,
            unitPrice: l.unitPrice,
            quantity: l.quantity,
            img: l.img,
            ...(l.pack ? { pack: l.pack } : {}),
          })),
          shipping: parsed.data.shipping,
          billing,
          delivery: {
            id: delivery.id,
            label: delivery.label,
            eta: delivery.eta,
            price: delivery.price,
          },
          pricing: {
            subtotal: pricing.subtotal,
            shipping: pricing.shipping,
            discount: pricing.discount,
            total: pricing.total,
            promoCode: pricing.promo?.code ?? null,
            ...(pricing.gst ? { gst: pricing.gst } : {}),
          },

          currency,
          marketing: marketing as Record<string, string | undefined>,
        },
      });

      try {
      await openRazorpayCheckout({
        key: intent.keyId,
        order_id: intent.razorpayOrderId,
        amount: intent.amountCents,
        currency: intent.currency,
        name: "CB16 Maison",
        description: `Order for ${parsed.data.customer.email}`,
        prefill: {
          email: parsed.data.customer.email,
          name: parsed.data.shipping.fullName,
          contact: parsed.data.shipping.phone,
        },
        theme: { color: "#C6A15B" },
        notes: { intent_id: intent.intentId },
        modal: {
          ondismiss: () => {
            void cancelFn({ data: { intentId: intent.intentId } });
            setSubmitting(false);
            setSubmitError("Payment cancelled. Your bag has been kept — you can retry any time.");
          },
        },
        handler: async (resp) => {
          try {
            const verified = await verifyFn({
              data: {
                intentId: intent.intentId,
                razorpayOrderId: resp.razorpay_order_id,
                razorpayPaymentId: resp.razorpay_payment_id,
                razorpaySignature: resp.razorpay_signature,
              },
            });
            // Local mirror for admin/account UI (localStorage-backed).
            await submitOrder({
              data: parsed.data,
              lines,
              pricing,
              delivery,
              marketing: readMarketing() ?? undefined,
              overrideId: verified.orderNumber,
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              estimatedDeliveryAt: new Date(
                Date.now() +
                  (delivery.id === "express" ? 2 : delivery.id === "boutique" ? 1 : 5) *
                    86_400_000,
              ).toISOString(),
              skipDelay: true,
            });
            clearCart();
            clearMarketing();
            navigate({ to: "/order/$id", params: { id: verified.orderNumber } });
          } catch (err) {
            setSubmitError(
              err instanceof Error
                ? `Payment verification failed: ${err.message}`
                : "Payment verification failed.",
            );
            setSubmitting(false);
          }
        },
      });
      } catch (openErr) {
        // The window failed to open: release this attempt's stock hold straight away.
        void cancelFn({ data: { intentId: intent.intentId } });
        throw openErr;
      }
      track(CIO_EVENTS.PAYMENT_STARTED, {
        email: parsed.data.customer.email,
        grand_total: pricing.total,
        currency,
        payment_method: "razorpay",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "We couldn't process this order.";
      if (/^Promo code not applied/i.test(message)) {
        promo.clear();
        setSubmitError(`${message} Your total has been updated — please review and place the order again.`);
      } else if (/refresh your bag|pricing changed|no longer available/i.test(message)) {
        // Prices moved between the check and Place Order: re-sync and ask the customer to review.
        await priceSync.resync();
        setSubmitError("Your bag was updated with the latest prices. Please review and accept them below.");
      } else {
        setSubmitError(message);
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
        <header className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#C6A15B]">Checkout</p>
          <h1
            className="mt-2 text-3xl text-black sm:text-4xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Complete Your Order
          </h1>
          <p className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-black/50">
            <Lock className="h-3.5 w-3.5 text-[#C6A15B]" /> Secure, encrypted checkout
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_380px]"
        >
          <div className="space-y-10">
            <Section title="1 · Customer">
              <CheckoutField
                label="Email"
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                error={pickError("customer.email")}
                maxLength={255}
              />
              <label className="mt-4 flex items-start gap-3 text-sm text-black/70">
                <input
                  type="checkbox"
                  checked={form.marketingOptIn}
                  onChange={(e) => setForm((f) => ({ ...f, marketingOptIn: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-[#C6A15B]"
                />
                Send me the CB16 Maison journal — new releases and private previews.
              </label>
              <div className="mt-4">
                <CheckoutField
                  label="GSTIN (optional — business orders)"
                  id="gstin"
                  name="gstin"
                  value={form.gstin}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))
                  }
                  error={pickError("customer.gstin")}
                  maxLength={15}
                />
              </div>
            </Section>


            <Section title="2 · Shipping Address">
              {book.signedIn && book.addresses.length > 0 ? (
                <SavedAddressPicker
                  book={book}
                  selectedId={selectedAddressId}
                  onSelect={selectSavedAddress}
                  errors={addressErrors("shipping")}
                />
              ) : (
                <>
                  <DeviceAddressesNotice book={book} />
                  {book.signedIn && book.loading && (
                    <p className="mb-4 text-sm text-black/50">Loading your saved addresses…</p>
                  )}
                  <AddressFieldset
                    legend="Ship to"
                    prefix="shipping"
                    value={form.shipping}
                    errors={addressErrors("shipping")}
                    onChange={(patch) =>
                      setForm((f) => ({ ...f, shipping: { ...f.shipping, ...patch } }))
                    }
                  />
                  {book.signedIn && !book.loading && (
                    <label className="mt-4 flex items-start gap-3 text-sm text-black/70">
                      <input
                        type="checkbox"
                        checked={saveNewAddress}
                        onChange={(e) => setSaveNewAddress(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-[#C6A15B]"
                      />
                      Save this address to my account for next time.
                    </label>
                  )}
                </>
              )}
            </Section>

            <Section title="3 · Delivery Method">
              <div className="space-y-3">
                {deliveryOptions.map((opt) => {
                  // Same charge the summary and the server use for this method.
                  const charge = deliveryCharge(
                    opt,
                    Math.max(0, pricing.subtotal - pricing.discount),
                    shippingConfig,
                    hasFreeShippingPack(lines),
                  );
                  return (

                  <OptionCard
                    key={opt.id}
                    name="delivery"
                    value={opt.id}
                    selected={form.delivery === opt.id}
                    onSelect={() => setForm((f) => ({ ...f, delivery: opt.id }))}
                    title={opt.label}
                    description={opt.eta}
                    icon={<Truck className="h-4 w-4 text-[#C6A15B]" />}
                    meta={
                      charge > 0
                        ? formatPrice(charge)
                        : opt.price > 0
                          ? "Complimentary"
                          : "Free"
                    }
                  />
                  );
                })}
              </div>
            </Section>

            <Section title="4 · Payment">
              <div className="space-y-3">
                <OptionCard
                  name="payment"
                  value="razorpay"
                  selected={form.paymentMethod === "razorpay"}
                  onSelect={() => setForm((f) => ({ ...f, paymentMethod: "razorpay" }))}
                  title="Pay Securely with Razorpay"
                  description="Cards · UPI · Net Banking · Wallets"
                  icon={<ShieldCheck className="h-4 w-4 text-[#C6A15B]" />}
                />
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-black/45">
                You'll complete payment in a secure Razorpay window.
              </p>
            </Section>

            <Section title="5 · Billing Address">
              <label className="mb-4 flex items-start gap-3 text-sm text-black/70">
                <input
                  type="checkbox"
                  checked={form.billingSameAsShipping}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, billingSameAsShipping: e.target.checked }))
                  }
                  className="mt-1 h-4 w-4 accent-[#C6A15B]"
                />
                Billing address is the same as shipping.
              </label>
              {!form.billingSameAsShipping && (
                <AddressFieldset
                  legend="Bill to"
                  prefix="billing"
                  value={form.billing}
                  errors={addressErrors("billing")}
                  onChange={(patch) =>
                    setForm((f) => ({ ...f, billing: { ...f.billing, ...patch } }))
                  }
                />
              )}
            </Section>

            <Section title="6 · Promo Code" optional>
              <PromoInput promo={promo} discount={pricing.discount} offers={offers} />
            </Section>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <CheckoutSummary lines={lines} pricing={pricing} delivery={delivery} />

            {submitError && (
              <p
                role="alert"
                className="mt-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {submitError}
              </p>
            )}

            <CartPriceNotice sync={priceSync} total={pricing.total} />

            <button
              type="submit"
              disabled={submitting || priceBlocked || promo.pending}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 border border-black bg-black px-6 py-3.5 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B] disabled:cursor-wait disabled:opacity-60"
            >
              <Lock className="h-3.5 w-3.5" />
              {submitting
                ? "Placing Order…"
                : priceSync.syncing && priceSync.changes.length === 0
                  ? "Checking latest prices…"
                  : `Place Order · ${formatPrice(pricing.total)}`}
            </button>
            <Link
              to="/cart"
              className="mt-3 inline-flex w-full items-center justify-center px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-black/70 transition hover:text-[#C6A15B]"
            >
              ← Back to Bag
            </Link>
          </aside>
        </form>
      </div>
    </div>
  );
}

function toShipping(a: SavedAddress): Address {
  return {
    fullName: a.fullName,
    line1: a.line1,
    line2: a.line2 ?? "",
    city: a.city,
    state: a.state ?? "",
    postalCode: a.postalCode,
    country: a.country,
    phone: a.phone,
  };
}

function Section({
  title,
  children,
  optional,
}: {
  title: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <section className="border-t border-black/10 pt-8 first:border-t-0 first:pt-0">
      <div className="mb-5 flex items-baseline gap-3">
        <h2 className="text-lg text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title}
        </h2>
        {optional && (
          <span className="text-[10px] uppercase tracking-[0.24em] text-black/40">Optional</span>
        )}
      </div>
      {children}
    </section>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <ShoppingBag className="mx-auto h-10 w-10 text-black/30" strokeWidth={1.25} />
      <h1 className="mt-6 text-2xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
        Your bag is empty
      </h1>
      <p className="mt-3 text-sm text-black/60">
        Add a fragrance to your bag before proceeding to checkout.
      </p>
      <Link
        to="/fragrances"
        className="mt-6 inline-flex items-center justify-center border border-black bg-black px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
      >
        Shop Fragrances
      </Link>
    </div>
  );
}
